import { useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { Coins, LogOut, MoreHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Logo } from "./Logo";
import { NAV } from "./nav";
import { useAuth } from "../context/AuthContext";

const itemCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
    isActive ? "bg-amber-500/10 text-amber-300 border border-amber-500/25" : "text-zinc-400 hover:text-white hover:bg-zinc-900 border border-transparent"
  }`;

const NavList = ({ onClick, prefix }) =>
  NAV.map((n) => (
    <NavLink key={n.to} to={n.to} end={n.to === "/"} className={itemCls} onClick={onClick} data-testid={`${prefix}-${n.id}`}>
      <n.icon className="h-4 w-4" />{n.label}
    </NavLink>
  ));

const Sidebar = () => {
  const { user, logout } = useAuth();
  return (
    <aside className="hidden md:flex md:w-64 lg:w-72 flex-col fixed inset-y-0 left-0 z-40 bg-zinc-950 border-r border-zinc-800/90 p-5 overflow-y-auto">
      <Logo />
      <Link to="/planos" className="mt-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-4 block" data-testid="sidebar-credits">
        <div className="eyebrow">Créditos de IA</div>
        <div className="font-display text-3xl font-extrabold gold-text mt-1">{user?.credits ?? 0}</div>
        <div className="text-xs text-zinc-400 mt-1">Plano {user?.plan_info?.name}</div>
      </Link>
      <nav className="mt-6 flex flex-col gap-1"><NavList prefix="sidebar-nav" /></nav>
      <button onClick={logout} className="mt-auto pt-6 flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-500 hover:text-rose-400" data-testid="sidebar-logout-button">
        <LogOut className="h-4 w-4" /> Sair
      </button>
    </aside>
  );
};

const TopBar = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const cur = NAV.find((n) => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)));
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <header className="sticky top-0 z-30 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/80 px-4 sm:px-6 py-3 flex items-center justify-between">
      <div className="md:hidden"><Logo size={30} compact /></div>
      <div className="hidden md:block text-sm text-zinc-400">{cur?.label}</div>
      <div className="flex items-center gap-2">
        <Link to="/planos" className="badge-gold" data-testid="topbar-credits-badge"><Coins className="h-3.5 w-3.5" />{user?.credits ?? 0} créditos</Link>
        <Link to="/perfil" className="h-9 w-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 text-black text-xs font-bold grid place-items-center" data-testid="topbar-avatar">{initials}</Link>
      </div>
    </header>
  );
};

const BottomNav = () => {
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const cls = ({ isActive }) => `flex flex-col items-center gap-1 px-2 py-1 text-[10px] font-medium ${isActive ? "text-amber-400" : "text-zinc-500"}`;
  return (
    <>
      <nav className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800/90 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex justify-around">
        {NAV.filter((n) => n.main).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} className={cls} data-testid={`bottom-nav-${n.id}`}>
            <n.icon className="h-5 w-5" />{n.label}
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} className="flex flex-col items-center gap-1 px-2 py-1 text-[10px] font-medium text-zinc-500" data-testid="bottom-nav-more">
          <MoreHorizontal className="h-5 w-5" />Mais
        </button>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-zinc-950 border-zinc-800 text-white rounded-t-3xl max-h-[85vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-white font-display">Menu</SheetTitle></SheetHeader>
          <div className="grid grid-cols-1 gap-1 mt-4"><NavList prefix="more-nav" onClick={() => setOpen(false)} /></div>
          <button onClick={logout} className="mt-4 flex items-center gap-3 px-3 py-2.5 text-sm text-rose-400" data-testid="more-logout-button"><LogOut className="h-4 w-4" /> Sair</button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export const Layout = () => (
  <div className="min-h-screen bg-[#09090B] grain">
    <Sidebar />
    <div className="md:pl-64 lg:pl-72 relative z-10">
      <TopBar />
      <main className="px-4 sm:px-6 lg:px-10 py-6 pb-28 md:pb-10 max-w-7xl mx-auto"><Outlet /></main>
    </div>
    <BottomNav />
  </div>
);
